import InfoIcon from '@mui/icons-material/Info';
import { Box } from '@mui/material';
import { getFactoringBalanceByPayers } from 'lib/api';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import CustomSwitch from 'lib/designSystem/CustomSwitch';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

const STAGES_TO_CONSIDER_NEW_DOCUMENTS = [
  'Creada',
  'En revisión',
  'Cotizada',
  'En giro',
  'Aceptada',
  'Borrador',
  'Creando',
];
const PayersConcentration = (props: any) => {
  const dispatch = useDispatch();
  const { selectedRequest } = props;
  const [loading, setLoading] = useState(false);
  const [concentrationsByPayers, setConcentrationsByPayers] = useState<any[]>(
    [],
  );
  const [proyectedConcentrationsByPayers, setProyectedConcentrationsByPayers] =
    useState<any[]>([]);
  const [showProyectedConcentrations, setShowProyectedConcentrations] =
    useState(false);

  const setUPConcentrationsByPayers = async () => {
    setLoading(true);
    try {
      const response = await getFactoringBalanceByPayers(dispatch, {
        identifier: selectedRequest.businessIdentifier,
        quoteId: selectedRequest.quoteId,
      });
      const assignationsWithAountInOperation = addAmountInOperation(response);
      setConcentrationsByPayers(
        assignationsWithAountInOperation.map((x: any) => ({
          ...x,
          id: x.stakeholderIdentifier,
        })),
      );
      const proyectedAssignations = proyectActualRequest(
        assignationsWithAountInOperation,
      );
      setProyectedConcentrationsByPayers(
        proyectedAssignations.map((x: any) => ({
          ...x,
          id: x.stakeholderIdentifier,
        })),
      );
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const addAmountInOperation = (assignations: any) => {
    const proyectedAssignations = [
      ...assignations.map((x: any) => ({
        ...x,
        totalDocumentsCount: x.totalDocumentsCount || x.documentsCount || 0,
        amountInOperation: 0,
      })),
    ];

    for (const document of selectedRequest.documents) {
      const assignationIndex = proyectedAssignations.findIndex(
        (assignation: any) =>
          assignation.stakeholderIdentifier === document.stakeholderIdentifier,
      );
      if (assignationIndex >= 0) {
        proyectedAssignations[assignationIndex] = {
          ...proyectedAssignations[assignationIndex],
          amountInOperation:
            proyectedAssignations[assignationIndex].amountInOperation +
            document.amount,
        };
      } else {
        proyectedAssignations.push({
          stakeholderName: document.stakeholderName,
          stakeholderIdentifier: document.stakeholderIdentifier,
          totalDocumentsCount: 0,
          amountUsed: 0,
          amountInOperation: document.amount,
        });
      }
    }
    return proyectedAssignations.sort(
      (a: any, b: any) => b.amountInOperation - a.amountInOperation,
    );
  };

  const proyectActualRequest = (assignations: any) => {
    const proyectedAssignations = [
      ...assignations.map((x: any) => ({
        ...x,
        totalDocumentsCount: x.totalDocumentsCount || x.documentsCount || 0,
        amountInOperation: 0,
      })),
    ];
    if (STAGES_TO_CONSIDER_NEW_DOCUMENTS.includes(selectedRequest.status)) {
      for (const document of selectedRequest.documents) {
        const assignationIndex = proyectedAssignations.findIndex(
          (assignation: any) =>
            assignation.stakeholderIdentifier ===
            document.stakeholderIdentifier,
        );
        if (assignationIndex >= 0) {
          proyectedAssignations[assignationIndex] = {
            ...proyectedAssignations[assignationIndex],
            totalDocumentsCount:
              proyectedAssignations[assignationIndex].totalDocumentsCount + 1,
            amountUsed:
              proyectedAssignations[assignationIndex].amountUsed +
              document.amount,
            amountInOperation:
              proyectedAssignations[assignationIndex].amountInOperation +
              document.amount,
          };
        } else {
          proyectedAssignations.push({
            stakeholderName: document.stakeholderName,
            stakeholderIdentifier: document.stakeholderIdentifier,
            totalDocumentsCount: 1,
            amountUsed: document.amount,
            amountInOperation: document.amount,
          });
        }
      }
    } else {
      for (const document of selectedRequest.documents) {
        const assignationIndex = proyectedAssignations.findIndex(
          (assignation: any) =>
            assignation.stakeholderIdentifier ===
            document.stakeholderIdentifier,
        );
        if (assignationIndex >= 0) {
          proyectedAssignations[assignationIndex] = {
            ...proyectedAssignations[assignationIndex],
            amountInOperation:
              proyectedAssignations[assignationIndex].amountInOperation +
              document.amount,
          };
        }
      }
    }
    return proyectedAssignations.sort(
      (a: any, b: any) => b.amountInOperation - a.amountInOperation,
    );
  };

  useEffect(() => {
    void setUPConcentrationsByPayers();
  }, []);

  const handleShowProyectedConcentrations = () => {
    setShowProyectedConcentrations(!showProyectedConcentrations);
  };

  const buttons: CustomButtonProps[] = [
    {
      color: 'primary',
      action: handleShowProyectedConcentrations,
      customButton: (
        <CustomSwitch
          leftText="Concentración actual"
          rightText="Concentración proyectada"
          onChange={handleShowProyectedConcentrations}
        />
      ),
    },
  ];

  const handleShowStakeholderDetails = (id: string) => {
    const row: any = concentrationsByPayers.find((x: any) => x.id === id);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.stakeholderIdentifier,
        name: row.stakeholderName,
      }),
    );
  };

  const payersHeaders = [
    { key: 'stakeholderName', label: 'Pagador' },
    {
      key: 'stakeholderIdentifier',
      label: 'Rut',
      handleAction: handleShowStakeholderDetails,
      icon: <InfoIcon fontSize="small" />,
    },
    { key: 'totalDocumentsCount', label: 'Documentos' },
    { key: 'amountInOperation', label: 'Monto en solicitud', type: 'money' },
    { key: 'amountUsed', label: 'Monto utilizado', type: 'money' },
    {
      key: 'concentrationLimit',
      label: 'Límite',
      type: 'money',
    },
    {
      key: 'conciliatedByBusinessAmount',
      label: 'Recaudado cliente',
      type: 'money',
    },
    {
      key: 'conciliatedByStakeholderAmount',
      label: 'Recaudado pagador',
      type: 'money',
    },
    {
      key: 'conciliatedTotalAmount',
      label: 'Recaudado total',
      type: 'money',
    },
  ];

  if (loading)
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          width: '100%',
        }}
      >
        <CenteredCircularProgress relative />
      </Box>
    );

  return (
    <div>
      <HeaderWithActions
        title="Acá puedes ver la concentración actual y proyectada de pagadores"
        variant="body1"
        buttons={buttons}
      />
      <EnhancedTable
        dataHeaders={payersHeaders}
        data={
          showProyectedConcentrations
            ? proyectedConcentrationsByPayers
            : concentrationsByPayers
        }
        rowsPerPageDefault={5}
        finalSumRow
        exportToFile="xlsx"
      />
    </div>
  );
};

export default PayersConcentration;
